import { useEffect } from "react";

// ── Domaine canonique du site vitrine ────────────────────────────────────────
// (modifie cette valeur si le domaine final diffère)
export const SITE_URL = "https://tchatix.ai";
export const SITE_NAME = "Tchatix";
export const DEFAULT_OG_IMAGE =
  "https://media.base44.com/images/public/6a69bb99139c43be5222264c/db430a032_Tchatix.png";
// Logo « tête seule » (carré) utilisé pour les partages sociaux / meta Google.

// ── Helpers head ──────────────────────────────────────────────────────────────
function upsertMeta(key, attr, content) {
  if (content == null || content === "") return;
  let el = document.head.querySelector(`meta[${key}="${attr}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, attr);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Injecte (ou remplace) un bloc JSON-LD identifié par son data-id.
export function setJsonLd(id, data) {
  const existing = document.head.querySelector(`script[data-seo="${id}"]`);
  if (existing) existing.remove();
  if (!data) return;
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-seo", id);
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}

// Pages marketing disposant d'un miroir /en/ (hreflang).
// /article/:slug et les pages auth/admin n'en ont pas (contenu FR uniquement).
const LOCALIZABLE_PATHS = new Set([
  "/", "/tarifs", "/simulateur", "/a-propos", "/contact",
  "/blog", "/securite-rgpd", "/securite-ia", "/cookies", "/confidentialite",
]);

// ── Composant <Seo /> ─────────────────────────────────────────────────────────
// Props :
//  title       — <title> (le nom du site est ajouté automatiquement)
//  description — meta description + og:description
//  path        — chemin canonique (ex: "/tarifs") → https://tchatix.ai/tarifs
//  image       — og:image / twitter:image
//  type        — og:type ("website" | "article")
//  noindex     — true → meta robots noindex,nofollow
//  jsonLd      — objet ou tableau d'objets JSON-LD à injecter
export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  noindex = false,
  jsonLd,
}) {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Agents IA sur mesure`;
  const base = SITE_URL.replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  // FR = forme canonique (on retire le préfixe /en éventuel).
  const frPath = p.startsWith("/en") && (p === "/en" || p.startsWith("/en/")) ? (p.slice(3) || "/") : p;
  const enPath = `/en${frPath === "/" ? "" : frPath}`;
  const canonical = base + frPath;
  const isLocalizable = LOCALIZABLE_PATHS.has(frPath);
  const frUrl = base + frPath;
  const enUrl = base + enPath;

  useEffect(() => {
    document.title = fullTitle;

    upsertMeta("name", "description", description);
    if (noindex) upsertMeta("name", "robots", "noindex, nofollow");

    // Open Graph
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", "fr_FR");

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    // Canonical (FR = forme canonique, même sur les routes /en/)
    upsertLink("canonical", canonical);

    // hreflang : alternate FR / EN / x-default pour les pages localisables.
    // On gère les trois via un attribut data-hreflang pour nettoyer les anciens.
    document.head.querySelectorAll('link[rel="alternate"][data-hreflang]').forEach((el) => el.remove());
    if (isLocalizable) {
      // hreflang EN retiré tant que le contenu /en/ n'est pas réellement traduit
      // (les routes /en existent mais affichent le contenu FR via LanguageSync).
      const alts = [
        { hreflang: "fr", href: frUrl },
        { hreflang: "x-default", href: frUrl },
      ];
      alts.forEach((a) => {
        const el = document.createElement("link");
        el.setAttribute("rel", "alternate");
        el.setAttribute("hreflang", a.hreflang);
        el.setAttribute("href", a.href);
        el.setAttribute("data-hreflang", a.hreflang);
        document.head.appendChild(el);
      });
    }

    // JSON-LD
    const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    blocks.forEach((b, i) => setJsonLd(`page-${i}`, b));
    // Nettoie les éventuels blocs excédentaires d'un rendu précédent
    if (blocks.length < 4) {
      for (let i = blocks.length; i < 4; i++) {
        const ex = document.head.querySelector(`script[data-seo="page-${i}"]`);
        if (ex) ex.remove();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullTitle, description, canonical, image, type, noindex, isLocalizable, frUrl, enUrl, JSON.stringify(jsonLd)]);

  return null;
}

// ── Builders JSON-LD réutilisables ────────────────────────────────────────────
export function organizationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    legalName: "Tchatix",
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    description: "Plateforme IA française souveraine dédiée aux agents IA sur mesure et à l'automatisation IA, sécurisée et conforme RGPD, conçue pour les PME et ETI.",
    founders: [
      { "@type": "Person", name: "Alexandre Pennec" },
      { "@type": "Person", name: "Stéphen Jarlegand" },
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: "Six-Fours-les-Plages",
      addressRegion: "Provence-Alpes-Côte d'Azur",
      addressCountry: "FR",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "contact@tchatix.ai",
      url: SITE_URL + "/contact",
      areaServed: "FR",
      availableLanguage: ["French", "English"],
    },
    sameAs: [
      "https://www.linkedin.com/company/tchatix",
      "https://www.youtube.com/@tchatix",
      "https://www.instagram.com/tchatix.ai",
    ],
  };
}

export function websiteLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: "fr-FR",
  };
}

export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: SITE_URL.replace(/\/$/, "") + (it.path || ""),
    })),
  };
}

export function faqLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function blogPostingLd(article, path) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.excerpt || "",
    image: article.cover_image_url || DEFAULT_OG_IMAGE,
    datePublished: article.created_date,
    dateModified: article.updated_date || article.created_date,
    author: { "@type": "Organization", name: "Tchatix — Agent SEO" },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: DEFAULT_OG_IMAGE },
    },
    mainEntityOfPage: SITE_URL.replace(/\/$/, "") + (path || ""),
    inLanguage: "fr-FR",
  };
}